// src/components/module/ventas/VentasList.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Box,
  Typography,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Stack,
  Fade,
  Tab,
  Tabs,
  alpha,
  Skeleton,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import {
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Add as AddIcon,
  PlayCircleOutline as PlayIcon,
  Download as DownloadIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  TrendingUp,
  CheckCircleOutline,
  Schedule,
  CancelOutlined,
  Storefront as StorefrontIcon,
  CreditCard as CreditCardIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { VentasApi } from "../../../services/api/VentasApi";
import { formatMoney, formatDate } from "../../../utils/format";
import { pdf } from "@react-pdf/renderer";
import { VentaDetailModal } from "./VentaDetailModal";
import { CreditDetailModal } from "./CreditDetailModal";
import { EnviarEmailModal } from "../common/EnviarEmailModal";
import PageLayout from "../../../components/layout/PageLayout";
import Swal from "sweetalert2";
import { toast } from "../../../utils/toast";
import { FacturaCompletaPDF } from "./facturas/FacturaCompletaPDF";
import { DarkModeContext } from "../../../context/darkModeContext";
import { useAuthorization } from "../../../hooks/useAuthorization";
import { FacturacionElectronicaApi, EstadoDocumento } from '../../../services/api/facturacionElectronicaApi';
import { useConfiguracionEmpresa, ConfiguracionEmpresa } from '../../../hooks/useConfiguracionEmpresa';

// ── Interfaces ──
interface ArticuloVenta {
  Id: number; Nombre: string; EAN: string; PrecioVenta: number;
  Cantidad: number; Descuento: number; Valor: number;
  Impuesto: Array<{ Id: number; Nombre: string; Valor: number }>;
}

interface DetalleVenta {
  id: number; fecha: string; numeroFactura: string;
  cliente: { id: number; nombre: string; identificacion: string; direccion: string; telefono: string; correo: string };
  sucursal: { id: number; nombre: string };
  vendedor: { id: number; nombre: string };
  articulos: ArticuloVenta[];
  subtotal: number; descuentoTotal: number;
  impuestos: Array<{ nombre: string; valor: number }>;
  totalAPagar: number; estado: string; saldoPendiente: number;
  tipoPago: { id: number; nombre: string }; observaciones?: string;
}

// ── Paleta Notion/Linear ──
const P = {
  light: {
    bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F",
    textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0",
    accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB",
    warning: "#D9730D", warningBg: "#FADEC9", error: "#E03E3E", errorBg: "#FBE4E4",
    purple: "#6940A5", purpleBg: "#EAE4F2",
    shadow: "0 1px 3px rgba(0,0,0,0.04)", hover: "rgba(55,53,47,0.04)",
  },
  dark: {
    bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6",
    textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444",
    accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A",
    warning: "#C77D48", warningBg: "#2E2215", error: "#D44C47", errorBg: "#3D2020",
    purple: "#9A6DD7", purpleBg: "#2A2040",
    shadow: "0 1px 3px rgba(0,0,0,0.2)", hover: "rgba(255,255,255,0.04)",
  },
};

const getEstadoStyle = (estado: string, dark: boolean) => {
  const p = dark ? P.dark : P.light;
  const s = estado.toLowerCase();
  if (s === "pagada") return { bg: p.successBg, color: p.success, dot: p.success, label: "Pagada" };
  if (s === "pendiente") return { bg: p.warningBg, color: p.warning, dot: p.warning, label: "Pendiente" };
  if (s === "abierta" || s === "en proceso") return { bg: p.accentBg, color: p.accent, dot: p.accent, label: "En Proceso" };
  if (s === "anulada") return { bg: p.errorBg, color: p.error, dot: p.error, label: "Anulada" };
  if (s === "crédito" || s === "credito") return { bg: p.purpleBg, color: p.purple, dot: p.purple, label: "Crédito" };
  return { bg: p.bgT, color: p.textS, dot: p.textS, label: estado };
};

// ── KPI Card ──
const KPICard: React.FC<{ label: string; value: string | number; icon: React.ReactElement; accent?: string; dark: boolean }> = ({ label, value, icon, accent, dark }) => {
  const p = dark ? P.dark : P.light;
  return (
    <Box sx={{
      p: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg,
      boxShadow: p.shadow, flex: 1, minWidth: 160,
      transition: "all 0.15s", "&:hover": { borderColor: p.borderH },
    }}>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Box sx={{
          width: 36, height: 36, borderRadius: "8px", bgcolor: accent || p.bgT,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: accent ? p.bg : p.textS, "& svg": { fontSize: 18 },
        }}>{icon}</Box>
        <Box>
          <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, letterSpacing: "0.02em" }}>{label}</Typography>
          <Typography sx={{ fontSize: "1.35rem", fontWeight: 700, color: p.text, fontFeatureSettings: "'tnum'" }}>{value}</Typography>
        </Box>
      </Stack>
    </Box>
  );
};

// ── Action Button ──
const ActionBtn: React.FC<{ tip: string; icon: React.ReactElement; onClick?: () => void; disabled?: boolean; dark: boolean; variant?: string }> = ({ tip, icon, onClick, disabled, dark, variant = "default" }) => {
  const p = dark ? P.dark : P.light;
  const c: Record<string, any> = {
    default: { bg: p.bgT, hover: p.borderH, text: p.textS },
    accent: { bg: p.accentBg, hover: dark ? "#1C4466" : "#D4E4F7", text: p.accent },
    error: { bg: p.errorBg, hover: dark ? "#4D2525" : "#F5CCCC", text: p.error },
    warning: { bg: p.warningBg, hover: dark ? "#3A2A18" : "#F5D0A9", text: p.warning },
  };
  const col = c[variant] || c.default;
  return (
    <Tooltip title={tip} arrow><span>
      <IconButton onClick={onClick} disabled={disabled} size="small" sx={{
        width: 30, height: 30, borderRadius: "7px", color: disabled ? p.textT : col.text,
        bgcolor: disabled ? "transparent" : col.bg, opacity: disabled ? 0.4 : 1,
        "& svg": { fontSize: 16 }, "&:hover": { bgcolor: disabled ? "transparent" : col.hover },
      }}>{icon}</IconButton>
    </span></Tooltip>
  );
};

// ── PDF Download (on-demand, no inline renderer) ──
const descargarPDF = async (
  venta: DetalleVenta,
  setLoading: (id: number, v: boolean) => void,
  empresa: ConfiguracionEmpresa,
) => {
  if (!venta.articulos?.length || !venta.numeroFactura) return;
  setLoading(venta.id, true);
  try {
    const blob = await pdf(
      <FacturaCompletaPDF
        venta={venta}
        configuracionEmpresa={empresa}
      />
    ).toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factura-${venta.numeroFactura}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch (e) {
    console.error("Error generando PDF:", e);
  } finally {
    setLoading(venta.id, false);
  }
};

// ── MAIN COMPONENT ──
export const VentasList: React.FC = () => {
  const navigate = useNavigate();
  const [ventas, setVentas] = useState<DetalleVenta[]>([]);
  const [selectedVenta, setSelectedVenta] = useState<DetalleVenta | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [creditVentaId, setCreditVentaId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [tabFilter, setTabFilter] = useState("todas");
  const [loading, setLoading] = useState(true);
  const [pdfLoading, setPdfLoading] = useState<Set<number>>(new Set());
  const setPdfLoadingFor = (id: number, v: boolean) =>
    setPdfLoading(prev => { const s = new Set(prev); v ? s.add(id) : s.delete(id); return s; });
  const [emailModal, setEmailModal] = useState<{ open: boolean; venta: DetalleVenta | null }>({ open: false, venta: null });
  const [cuotasVencidas, setCuotasVencidas] = useState<Record<number, number>>({});
  const { requestAuthorization, AuthorizationComponent } = useAuthorization();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;

  useEffect(() => { loadVentas(); loadCuotasVencidas(); }, []);

  const { empresa } = useConfiguracionEmpresa();
  const [docsDian, setDocsDian] = useState<Record<number, { Id: number; Estado: EstadoDocumento; CUFE: string | null }>>({});

  const loadVentas = async () => {
    try {
      setLoading(true);
      const lista = await VentasApi.getVentas();
      setVentas(lista);
      // Estado DIAN de todas las ventas en una sola peticion (evita N+1).
      // Si falla, el listado sigue funcionando sin la columna.
      try {
        setDocsDian(await FacturacionElectronicaApi.estadosPorVentas(lista.map((v: any) => v.id)));
      } catch { setDocsDian({}); }
    } catch { toast.error("No se pudieron cargar las ventas"); } finally { setLoading(false); }
  };

  const loadCuotasVencidas = async () => {
    const data = await VentasApi.getCuotasVencidasPorVenta();
    setCuotasVencidas(data);
  };

  const handleRetomarVenta = async (v: DetalleVenta) => {
    try {
      const s = v.estado.toLowerCase();
      if (s === "en proceso" || s === "abierta") {
        const r = await VentasApi.getVentaEnProceso(v.id);
        navigate("/ventas/new", { state: { ventaPendiente: r, modo: "retomar" } });
      } else if (s === "pendiente") navigate(`/ventas/continue?id=${v.numeroFactura}`);
    } catch { toast.error("No se pudo retomar la venta"); }
  };

  const handleCancelarVenta = async (v: DetalleVenta) => {
    const r = await Swal.fire({ title: "¿Cancelar venta?", text: "Se devolverá el stock al inventario", icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No", confirmButtonColor: p.error, reverseButtons: true });
    if (r.isConfirmed) { await VentasApi.cancelarVentaEnProceso(v.id); await loadVentas(); toast.success("Cancelada"); }
  };

  const handleAnularVenta = async (v: DetalleVenta) => {
    const r = await Swal.fire({
      title: "¿Anular factura?",
      text: "Esta acción no se puede deshacer",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: p.error,
      confirmButtonText: "Sí, anular",
      cancelButtonText: "No",
      reverseButtons: true,
    });
    if (!r.isConfirmed) return;

    const { value: observacion } = await Swal.fire({
      title: "Motivo de anulación",
      input: "textarea",
      inputPlaceholder: "Ingrese el motivo o información relevante...",
      inputAttributes: { "aria-label": "Motivo de anulación" },
      showCancelButton: true,
      confirmButtonText: "Continuar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: p.error,
      reverseButtons: true,
      inputValidator: (value) => {
        if (!value?.trim()) return "Debe ingresar un motivo para continuar";
        return null;
      },
    });
    if (!observacion) return;

    const authCode = await requestAuthorization("ANULAR_VENTA", "Anular Venta", { idVenta: v.id, codigoFactura: v.numeroFactura, fechaCreacionFactura: v.fecha, monto: v.totalAPagar, cliente: v.cliente.nombre });
    if (authCode === null) return;
    Swal.fire({ title: "Procesando...", allowOutsideClick: false, showConfirmButton: false, willOpen: () => Swal.showLoading() });
    try {
      await VentasApi.cancelarVenta(v.id, "anulacion", observacion.trim(), authCode || undefined);
      await loadVentas();
      Swal.close(); toast.success("Anulada");
    } catch (err: any) {
      Swal.close(); toast.error(err.message || "Ocurrió un error inesperado");
    }
  };

  // ── KPIs ──
  const kpis = useMemo(() => {
    const total = ventas.length;
    const pagadas = ventas.filter(v => v.estado.toLowerCase() === "pagada").length;
    const pendientes = ventas.filter(v => v.estado.toLowerCase() === "pendiente").length;
    const enProceso = ventas.filter(v => ["abierta", "en proceso"].includes(v.estado.toLowerCase())).length;
    const anuladas = ventas.filter(v => v.estado.toLowerCase() === "anulada").length;
    const credito = ventas.filter(v => ["crédito", "credito"].includes(v.estado.toLowerCase())).length;
    const monto = ventas.filter(v => v.estado.toLowerCase() === "pagada").reduce((s, v) => s + v.totalAPagar, 0);
    return { total, pagadas, pendientes, enProceso, anuladas, credito, monto };
  }, [ventas]);

  // ── Filtered ──
  const filtered = useMemo(() => {
    let items = ventas;
    if (tabFilter !== "todas") {
      if (tabFilter === "en proceso") items = items.filter(v => ["abierta", "en proceso"].includes(v.estado.toLowerCase()));
      else if (tabFilter === "crédito") items = items.filter(v => ["crédito", "credito"].includes(v.estado.toLowerCase()));
      else items = items.filter(v => v.estado.toLowerCase() === tabFilter);
    }
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      items = items.filter(v => (v.numeroFactura || "").toLowerCase().includes(s) || v.cliente.nombre.toLowerCase().includes(s) || v.cliente.identificacion.toLowerCase().includes(s));
    }
    return items;
  }, [ventas, tabFilter, searchTerm]);

  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const tabs = [
    { key: "todas", label: "Todas", count: kpis.total },
    { key: "pagada", label: "Pagadas", count: kpis.pagadas },
    { key: "pendiente", label: "Pendientes", count: kpis.pendientes },
    { key: "crédito", label: "Crédito", count: kpis.credito },
    { key: "en proceso", label: "En Proceso", count: kpis.enProceso },
    { key: "anulada", label: "Anuladas", count: kpis.anuladas },
  ];

  const thStyle = { fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase" as const, letterSpacing: "0.04em", borderBottom: `1px solid ${p.border}`, py: 1.2, bgcolor: p.bgS };
  const tdStyle = { borderBottom: `1px solid ${p.border}`, py: 1.5, fontSize: "0.84rem" };

  if (loading) return (
    <PageLayout icon={StorefrontIcon} title="Ventas">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress size={36} sx={{ color: p.accent }} />
        </Box>
      </PageLayout>
  );

  return (
    <>
    <PageLayout icon={StorefrontIcon} title="Ventas">
        <Box sx={{ p: 3 }}>
          {/* KPIs */}
          <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: "wrap", gap: 2, "& > *": { minWidth: { xs: "calc(50% - 8px)", md: "auto" } } }}>
            <KPICard label="Total Ventas" value={kpis.total} icon={<ReceiptIcon />} dark={darkMode} />
            <KPICard label="Pagadas" value={kpis.pagadas} icon={<CheckCircleOutline />} accent={p.successBg} dark={darkMode} />
            <KPICard label="Pendientes" value={kpis.pendientes} icon={<Schedule />} accent={p.warningBg} dark={darkMode} />
            <KPICard label="Crédito" value={kpis.credito} icon={<CreditCardIcon />} accent={p.purpleBg} dark={darkMode} />
            <KPICard label="Monto Pagado" value={formatMoney(kpis.monto)} icon={<TrendingUp />} accent={p.accentBg} dark={darkMode} />
          </Stack>

          {/* Main Card */}
          <Paper elevation={0} sx={{ border: `1px solid ${p.border}`, borderRadius: "12px", overflow: "hidden", bgcolor: p.bg, boxShadow: p.shadow }}>
            {/* Header */}
            <Box sx={{ px: 3, pt: 2.5, pb: 0, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
              <Box>
                <Typography sx={{ fontSize: "1.1rem", fontWeight: 700, color: p.text }}>Historial de Ventas</Typography>
                <Typography sx={{ fontSize: "0.82rem", color: p.textT, mt: 0.3 }}>{filtered.length} {filtered.length === 1 ? "venta" : "ventas"}</Typography>
              </Box>
              <Stack direction="row" spacing={1.5}>
                <Button onClick={() => { loadVentas(); loadCuotasVencidas(); }} startIcon={<RefreshIcon sx={{ fontSize: 16 }} />} sx={{ color: p.textS, fontSize: "0.82rem", borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>
                  Actualizar
                </Button>
                <Link to="/ventas/new" style={{ textDecoration: "none" }}>
                  <Button variant="contained" disableElevation startIcon={<AddIcon />} sx={{ bgcolor: p.accent, color: "#FFF", px: 2.5, py: 1, fontSize: "0.84rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
                    Nueva Venta
                  </Button>
                </Link>
              </Stack>
            </Box>

            {/* Search + Tabs */}
            <Box sx={{ px: 3, mt: 2 }}>
              <TextField
                size="small" placeholder="Buscar por factura, cliente o identificación..."
                value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: p.textT }} /></InputAdornment> }}
                sx={{
                  width: { xs: "100%", md: 360 }, mb: 1,
                  "& .MuiInputBase-root": { fontSize: "0.84rem", borderRadius: "8px", bgcolor: p.bgS, border: `1px solid ${p.border}`, px: 1, "&:hover": { borderColor: p.borderH }, "&.Mui-focused": { borderColor: p.accent }, "& fieldset": { border: "none" } },
                }}
              />
              <Tabs value={tabFilter} onChange={(_, v) => { setTabFilter(v); setPage(0); }} variant="scrollable" scrollButtons="auto"
                TabIndicatorProps={{ sx: { bgcolor: p.accent, height: 2, borderRadius: 1 } }}
                sx={{ minHeight: 40, "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontSize: "0.82rem", fontWeight: 500, color: p.textT, px: 1.5, mr: 0.5, "&.Mui-selected": { color: p.text, fontWeight: 600 } } }}>
                {tabs.map(t => (
                  <Tab key={t.key} value={t.key} label={
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <span>{t.label}</span>
                      {t.count > 0 && <Box component="span" sx={{ fontSize: "0.7rem", fontWeight: 600, bgcolor: tabFilter === t.key ? alpha(p.accent, 0.15) : p.bgT, color: tabFilter === t.key ? p.accent : p.textT, px: 0.8, py: 0.15, borderRadius: "5px" }}>{t.count}</Box>}
                    </Stack>
                  } />
                ))}
              </Tabs>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {["N° Factura", "Fecha", "Cliente", "Identificación", "Total", "Estado", "DIAN", ""].map(h => (
                      <TableCell key={h} sx={thStyle} align={h === "Total" ? "right" : h === "" ? "center" : "left"}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginated.length === 0 ? (
                    <TableRow><TableCell colSpan={8} sx={{ py: 6, textAlign: "center", borderBottom: "none" }}>
                      <Typography sx={{ fontSize: "0.88rem", color: p.textT }}>No se encontraron ventas</Typography>
                    </TableCell></TableRow>
                  ) : paginated.map(v => {
                    const est = getEstadoStyle(v.estado, darkMode);
                    const sLow = v.estado.toLowerCase();
                    return (
                      <TableRow key={`${v.id}-${v.numeroFactura}`} sx={{ "&:hover": { bgcolor: p.hover }, transition: "background 0.1s" }}>
                        <TableCell sx={{ ...tdStyle, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 600, color: p.accent, fontSize: "0.82rem" }}>{v.numeroFactura || "—"}</TableCell>
                        <TableCell sx={{ ...tdStyle, color: p.textS }}>{formatDate(v.fecha)}</TableCell>
                        <TableCell sx={{ ...tdStyle, fontWeight: 500 }}>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Box sx={{ width: 28, height: 28, borderRadius: "7px", bgcolor: p.bgT, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.72rem", fontWeight: 700, color: p.textS, flexShrink: 0 }}>{v.cliente.nombre[0]}</Box>
                            <Typography sx={{ fontSize: "0.84rem", fontWeight: 500, color: p.text }}>{v.cliente.nombre}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell sx={{ ...tdStyle, color: p.textT, fontFamily: "'IBM Plex Mono', monospace", fontSize: "0.8rem" }}>{v.cliente.identificacion}</TableCell>
                        <TableCell align="right" sx={{ ...tdStyle, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace", color: p.success }}>{formatMoney(v.totalAPagar)}</TableCell>
                        <TableCell sx={tdStyle}>
                          <Stack direction="row" alignItems="center" spacing={0.8}>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, px: 1.5, py: 0.4, borderRadius: "6px", bgcolor: est.bg, color: est.color, fontSize: "0.78rem", fontWeight: 600, "& > span": { width: 6, height: 6, borderRadius: "50%", bgcolor: est.dot } }}>
                              <span />{est.label}
                            </Box>
                            {(sLow === "crédito" || sLow === "credito") && cuotasVencidas[v.id] > 0 && (
                              <Tooltip title={`${cuotasVencidas[v.id]} cuota${cuotasVencidas[v.id] > 1 ? 's' : ''} vencida${cuotasVencidas[v.id] > 1 ? 's' : ''}`} arrow>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, px: 1, py: 0.3, borderRadius: "6px", bgcolor: p.errorBg, color: p.error, fontSize: "0.72rem", fontWeight: 700, cursor: "default" }}>
                                  <CreditCardIcon sx={{ fontSize: 12 }} />
                                  {cuotasVencidas[v.id]}
                                </Box>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell sx={tdStyle}>
                          {(() => {
                            const d = docsDian[v.id];
                            if (!d) return <Typography sx={{ color: p.textT, fontSize: "0.78rem" }}>—</Typography>;
                            const ok = d.Estado === "VALIDADO";
                            const malo = d.Estado === "RECHAZADO" || d.Estado === "ERROR";
                            return (
                              <Box sx={{
                                display: "inline-flex", px: 0.9, py: 0.2, borderRadius: "6px",
                                fontSize: "0.72rem", fontWeight: 700,
                                bgcolor: ok ? p.successBg : malo ? p.errorBg : p.warningBg,
                                color: ok ? p.success : malo ? p.error : p.warning,
                              }}>
                                {ok ? "Facturada" : malo ? "Rechazada" : d.Estado}
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="center" sx={tdStyle}>
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            <ActionBtn tip="Ver detalle" icon={<VisibilityIcon />} onClick={() => { setSelectedVenta(v); setDetailOpen(true); }} dark={darkMode} />
                            {sLow === "abierta" && <>
                              <ActionBtn tip="Continuar" icon={<PlayIcon />} onClick={() => handleRetomarVenta(v)} dark={darkMode} variant="accent" />
                              <ActionBtn tip="Cancelar" icon={<CancelIcon />} onClick={() => handleCancelarVenta(v)} dark={darkMode} variant="error" />
                            </>}
                            {sLow === "pendiente" && <>
                              <ActionBtn tip="Continuar" icon={<PlayIcon />} onClick={() => handleRetomarVenta(v)} dark={darkMode} variant="accent" />
                              <ActionBtn tip="Anular" icon={<BlockIcon />} onClick={() => handleAnularVenta(v)} dark={darkMode} variant="warning" />
                            </>}
                            {(sLow === "crédito" || sLow === "credito") && (
                              <ActionBtn tip="Ver cuotas" icon={<CreditCardIcon />} onClick={() => { setCreditVentaId(v.id); setCreditOpen(true); }} dark={darkMode} variant="accent" />
                            )}
                            {sLow !== "en proceso" && sLow !== "abierta" && (<>
                              <ActionBtn
                                tip="Descargar PDF"
                                icon={pdfLoading.has(v.id) ? <CircularProgress size={14} /> : <DownloadIcon />}
                                onClick={() => descargarPDF(v, setPdfLoadingFor, empresa)}
                                disabled={pdfLoading.has(v.id)}
                                dark={darkMode}
                              />
                              <ActionBtn
                                tip="Enviar por correo"
                                icon={<EmailIcon />}
                                onClick={() => setEmailModal({ open: true, venta: v })}
                                dark={darkMode}
                                variant="accent"
                              />
                            </>)}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div" count={filtered.length} page={page}
              onPageChange={(_, n) => setPage(n)} rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              labelRowsPerPage="Filas:" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: `1px solid ${p.border}`, "& .MuiTablePagination-root": { fontSize: "0.78rem" } }}
            />
          </Paper>

          <VentaDetailModal venta={selectedVenta} open={detailOpen} onClose={() => { setDetailOpen(false); setSelectedVenta(null); }} />
          {creditVentaId && (
            <CreditDetailModal
              ventaId={creditVentaId}
              open={creditOpen}
              onClose={() => { setCreditOpen(false); setCreditVentaId(null); }}
              onCuotaPagada={loadVentas}
            />
          )}
          <EnviarEmailModal
            open={emailModal.open}
            onClose={() => setEmailModal({ open: false, venta: null })}
            codigoDocumento={emailModal.venta?.numeroFactura || ''}
            tipoDocumento="factura"
            correoCliente={emailModal.venta?.cliente?.correo || ''}
            darkMode={darkMode}
            onEnviar={async (emailDestino) => {
              await VentasApi.enviarFacturaPorEmail({
                codigoFactura: emailModal.venta!.numeroFactura,
                emailDestino,
              });
            }}
          />
        </Box>
      </PageLayout>
      <AuthorizationComponent />
    </>
  );
};
